import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Clock,
  MapPin,
  Building2,
  AlertTriangle,
  ClipboardList,
  Phone,
  PlayCircle,
  FileText,
  CheckCircle2,
} from 'lucide-react';
import { format, isToday } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { OrdenTrabajo } from '@/contexts/OrdenesTrabajoContext';

interface Props {
  ordenes: (OrdenTrabajo & { cliente_nombre?: string })[];
}

const prioridadColors: Record<string, string> = {
  urgente: 'bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/40',
  alta: 'bg-orange-500/15 text-orange-600 dark:text-orange-400 border-orange-500/40',
  media: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40',
  baja: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/40',
};

const accentByEstado: Record<string, string> = {
  asignada: 'border-l-blue-500',
  en_progreso: 'border-l-purple-500',
  creada: 'border-l-muted-foreground/40',
};

export const MiCalendarioHoy: React.FC<Props> = ({ ordenes }) => {
  const navigate = useNavigate();

  const ordenesHoy = useMemo(() => {
    return ordenes
      .filter(
        (o) =>
          o.fecha_limite &&
          isToday(new Date(o.fecha_limite)) &&
          o.estado !== 'finalizada' &&
          o.estado !== 'cancelada'
      )
      .sort((a, b) => {
        const ha = a.hora_ejecucion || '99:99';
        const hb = b.hora_ejecucion || '99:99';
        return ha.localeCompare(hb);
      });
  }, [ordenes]);

  if (ordenesHoy.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground border border-dashed border-border/60 rounded-lg bg-card/30">
        <CheckCircle2 className="w-14 h-14 mx-auto mb-4 opacity-50" />
        <p className="text-lg font-medium">Sin trabajos asignados para hoy</p>
        <p className="text-sm">Disfruta del día o consulta tu semana.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {format(new Date(), "EEEE d 'de' MMMM", { locale: es })} ·{' '}
        <span className="font-medium text-foreground">
          {ordenesHoy.length} {ordenesHoy.length === 1 ? 'tarea' : 'tareas'}
        </span>
      </p>

      {ordenesHoy.map((orden) => {
        const accent = accentByEstado[orden.estado] || 'border-l-muted';
        const direccion = orden.cliente?.direccion;
        const poblacion = orden.cliente?.poblacion;
        const direccionCompleta = [direccion, poblacion].filter(Boolean).join(', ');
        const tienePartes = (orden.partes_count || 0) > 0;
        const isUrgente = orden.prioridad === 'urgente';

        const accionLabel = tienePartes
          ? 'Ver / continuar parte'
          : orden.estado === 'en_progreso'
          ? 'Crear parte'
          : 'Empezar';
        const ActionIcon = tienePartes ? FileText : PlayCircle;

        return (
          <Card key={orden.id} className={`border-l-4 ${accent}`}>
            <CardContent className="p-3 sm:p-4 space-y-3">
              <div className="flex items-start justify-between gap-2 sm:gap-3 flex-wrap">
                <div className="flex items-start gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className="mt-0.5 shrink-0">
                    {isUrgente ? (
                      <AlertTriangle className="w-5 h-5 text-destructive" />
                    ) : (
                      <ClipboardList className="w-5 h-5 text-primary" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {orden.hora_ejecucion && (
                        <Badge variant="outline" className="text-xs gap-1 bg-muted/50">
                          <Clock className="w-3 h-3" />
                          {orden.hora_ejecucion.slice(0, 5)}
                        </Badge>
                      )}
                      <span className="font-mono text-xs text-muted-foreground">
                        {orden.numero_ot}
                      </span>
                      <span className="font-semibold truncate min-w-0 w-full sm:w-auto">{orden.titulo}</span>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1 min-w-0">
                      <Building2 className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{orden.cliente?.nombre || orden.cliente_nombre}</span>
                    </div>
                  </div>
                </div>
                <Badge variant="outline" className={`text-xs shrink-0 ${prioridadColors[orden.prioridad]}`}>
                  {orden.prioridad}
                </Badge>
              </div>

              {direccionCompleta && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    direccionCompleta
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MapPin className="w-4 h-4 shrink-0" />
                  <span className="truncate">{direccionCompleta}</span>
                </a>
              )}

              {orden.cliente?.telefono && (
                <a
                  href={`tel:${orden.cliente.telefono}`}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Phone className="w-4 h-4" />
                  {orden.cliente.telefono}
                </a>
              )}

              {orden.descripcion && (
                <p className="text-sm text-foreground/80 line-clamp-2">{orden.descripcion}</p>
              )}

              <div className="flex justify-stretch sm:justify-end pt-1">
                <Button
                  size="sm"
                  className="gap-2 w-full sm:w-auto"
                  onClick={() =>
                    navigate(`/partes-trabajo?ordenId=${orden.id}`)
                  }
                >
                  <ActionIcon className="w-4 h-4" />
                  {accionLabel}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
